/**
 * Модалка «Услуга или документ» справочника экологии (ecology-catalog, макет панели администратора
 * 16.09.2026). Поля макета — название, тип, срок действия / периодичность, срок разработки,
 * согласование, обязательные категории, «Показывать клиенту в заказе» — плюс структурные поля,
 * без которых кабинет не посчитает светофор: срок в годах или периодичность с датой сдачи,
 * «Ведётся строкой в пульте», виды изменений «Когда обновлять», документы, после которых позиция
 * требует актуализации (ПЭК, НДВ, НМУ — после инвентаризации, ответ заказчика 17.09), и описание для
 * клиента. У «Обучения по экологической безопасности» галочки «в заказе» нет: строка пульта берётся из
 * журнала обучения, а заказ оформляется через «Обучение».
 *
 * Правила формы — зеркало `domain.NormalizeEcoCatalogInput` (сервер проверяет авторитетно): поля,
 * не относящиеся к выбранному типу, скрываются и уходят пустыми. Справочное «Условие» правится в
 * матрице «Кому что нужно» и здесь сохраняется без изменений.
 */
import { useEffect, useId, useState, type FormEvent } from 'react'
import {
  adminApi,
  type EcoCatalogItem,
  type EcoCatalogItemInput,
  type EcoPeriod,
  type EcoQuestion,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import {
  ECO_CATEGORIES,
  ECO_CATEGORY_LABELS,
  ECO_KIND_OPTIONS,
  ECO_REPORT_SECTIONS,
  ECO_SECTION_LABELS,
  ECO_PERIOD_LABELS,
  ECO_UPDATE_TRIGGERS,
  ECO_UPDATE_TRIGGER_LABELS,
  ecoItemToInput,
  formatEcoDeadline,
  parseEcoDeadline,
  toggleCanonical,
} from '@/lib/ecology'
import { cn } from '@/lib/utils'
import { LabeledCheckbox } from '@/sout/labeled-checkbox'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { DateInput } from '@/design-system/components/primitives/date-input'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'

/** Выделение выбранной категории — как у выбора вариантов в формах проекта. */
const SELECTED_OPTION = 'border-accent bg-accent-bg text-accent-txt hover:bg-accent-bg'

/** Значение «периодичность не задана»: Radix Select не допускает пустое значение пункта. */
const NO_PERIOD = 'none'

/** Периодичности в порядке выбора. */
const PERIODS = Object.keys(ECO_PERIOD_LABELS) as Exclude<EcoPeriod, ''>[]

/** Черновик формы: числа — строками, как их вводит пользователь. */
interface Draft {
  input: EcoCatalogItemInput
  validityYears: string
  deadline: string
}

const EMPTY_INPUT: EcoCatalogItemInput = {
  name: '',
  kind: 'document',
  section: 'projects',
  tracked: true,
  validityYears: null,
  validityNote: '',
  period: '',
  deadlineDay: null,
  deadlineMonth: null,
  developmentTerm: '',
  approval: '',
  clientDescription: '',
  categories: [],
  conditionNote: '',
  updateTriggers: [],
  dependsOn: [],
  orderQuestionIds: [],
  showInOrder: true,
  outdatedBefore: '',
  orgWide: false,
}

function draftFrom(item: EcoCatalogItem | null): Draft {
  const input = item ? ecoItemToInput(item) : EMPTY_INPUT
  return {
    input,
    validityYears: input.validityYears === null ? '' : String(input.validityYears),
    deadline: formatEcoDeadline(input.deadlineDay, input.deadlineMonth),
  }
}

interface DraftErrors {
  name?: string
  validityYears?: string
  period?: string
  deadline?: string
}

function validate(d: Draft): DraftErrors {
  const errors: DraftErrors = {}
  if (!d.input.name.trim()) errors.name = 'Укажите название'
  if (d.input.kind === 'document' && d.validityYears.trim()) {
    const years = Number(d.validityYears)
    if (!Number.isInteger(years) || years < 1 || years > 100) {
      errors.validityYears = 'Целое число от 1 до 100'
    }
  }
  if (d.input.kind === 'report') {
    if (d.input.period === '' && d.input.tracked) {
      errors.period = 'Укажите периодичность — позиция ведётся в пульте'
    }
    if (d.input.period === 'yearly' && d.deadline.trim() && !parseEcoDeadline(d.deadline)) {
      errors.deadline = 'Дата в формате ДД.ММ, например 25.03'
    }
  }
  return errors
}

/** Тело запроса: поля, скрытые для выбранного типа, уходят пустыми. */
function toInput(d: Draft): EcoCatalogItemInput {
  const isDocument = d.input.kind === 'document'
  const deadline = !isDocument && d.input.period === 'yearly' ? parseEcoDeadline(d.deadline) : null
  return {
    ...d.input,
    name: d.input.name.trim(),
    validityYears: isDocument && d.validityYears.trim() ? Number(d.validityYears) : null,
    period: isDocument ? '' : d.input.period,
    deadlineDay: deadline?.day ?? null,
    deadlineMonth: deadline?.month ?? null,
    updateTriggers: isDocument ? d.input.updateTriggers : [],
    dependsOn: isDocument ? d.input.dependsOn : [],
  }
}

export function CatalogItemDialog({
  open,
  item,
  items,
  questions,
  onClose,
  onSaved,
}: {
  open: boolean
  /** Правка позиции; null — новая позиция. */
  item: EcoCatalogItem | null
  /** Действующий справочник — источник документов для зависимостей. */
  items: EcoCatalogItem[]
  /** Вопросы опросника — «Вопросы при заказе» этой услуги (ecology-order). */
  questions: EcoQuestion[]
  onClose: () => void
  onSaved: (saved: EcoCatalogItem) => void
}) {
  const formId = useId()
  const [draft, setDraft] = useState<Draft>(() => draftFrom(item))
  const [showErrors, setShowErrors] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Каждое открытие начинается с актуальной позиции (или пустой формы).
  useEffect(() => {
    if (!open) return
    setDraft(draftFrom(item))
    setShowErrors(false)
    setError(null)
  }, [open, item])

  const errors = validate(draft)
  const shown = showErrors ? errors : {}
  const title = item ? `Изменить: ${item.name}` : 'Новая услуга или документ'

  function patch(next: Partial<EcoCatalogItemInput>) {
    setDraft((d) => ({ ...d, input: { ...d.input, ...next } }))
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (saving) return
    if (Object.keys(errors).length > 0) {
      setShowErrors(true)
      return
    }
    setError(null)
    setSaving(true)
    try {
      const input = toInput(draft)
      const saved = item
        ? await adminApi.ecology.updateItem(item.id, input)
        : await adminApi.ecology.createItem(input)
      onSaved(saved)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить позицию справочника'))
    } finally {
      setSaving(false)
    }
  }

  const { input } = draft
  const isDocument = input.kind === 'document'
  // Зависеть можно от других документов справочника (не от себя).
  const dependencyOptions = items.filter((i) => i.kind === 'document' && i.id !== item?.id)

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !saving) onClose()
      }}
    >
      <DialogContent title={title} className="max-w-2xl">
        <Card
          scrollable
          className="max-h-dialog"
          header={title}
          footer={
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={onClose} disabled={saving}>
                Отмена
              </Button>
              <Button type="submit" form={formId} disabled={saving}>
                {saving ? 'Сохранение…' : 'Сохранить'}
              </Button>
            </div>
          }
        >
          <form id={formId} onSubmit={submit} noValidate>
            {error ? (
              <Alert variant="danger" className="mb-4">
                {error}
              </Alert>
            ) : null}

            <Field label="Название" required error={shown.name} className="mt-0">
              {(props) => (
                <Input
                  {...props}
                  value={input.name}
                  invalid={Boolean(shown.name)}
                  onChange={(e) => patch({ name: e.target.value })}
                />
              )}
            </Field>

            <div className="my-4 grid grid-cols-1 gap-4 nw:grid-cols-2">
              <Field label="Тип" className={cn('my-0', isDocument && 'nw:col-span-2')}>
                {(props) => (
                  <Select
                    value={input.kind}
                    onValueChange={(v) => {
                      const kind = v as EcoCatalogItemInput['kind']
                      // Подраздел следует за видом: у документа он один, отчётность по умолчанию —
                      // в «Отчётности» (ecology-lab-control).
                      patch({ kind, section: kind === 'document' ? 'projects' : 'reports' })
                    }}
                  >
                    <SelectTrigger id={props.id} aria-label="Тип позиции" className="text-left">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ECO_KIND_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              {!isDocument ? (
                <Field
                  label="Подраздел у клиента"
                  hint="Где клиент видит строку позиции"
                  className="my-0"
                >
                  {(props) => (
                    <Select
                      value={input.section}
                      onValueChange={(v) => patch({ section: v as EcoCatalogItemInput['section'] })}
                    >
                      <SelectTrigger
                        id={props.id}
                        aria-label="Подраздел у клиента"
                        className="text-left"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ECO_REPORT_SECTIONS.map((sec) => (
                          <SelectItem key={sec} value={sec}>
                            {ECO_SECTION_LABELS[sec]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
              ) : null}

              {isDocument ? (
                <Field
                  label="Срок действия, лет"
                  hint="Пусто — бессрочно"
                  error={shown.validityYears}
                  className="my-0"
                >
                  {(props) => (
                    <Input
                      {...props}
                      type="number"
                      min={1}
                      max={100}
                      value={draft.validityYears}
                      invalid={Boolean(shown.validityYears)}
                      onChange={(e) => setDraft((d) => ({ ...d, validityYears: e.target.value }))}
                    />
                  )}
                </Field>
              ) : (
                <Field label="Периодичность" error={shown.period} className="my-0">
                  {(props) => (
                    <Select
                      value={input.period === '' ? NO_PERIOD : input.period}
                      onValueChange={(v) =>
                        patch({ period: (v === NO_PERIOD ? '' : v) as EcoPeriod })
                      }
                    >
                      <SelectTrigger
                        id={props.id}
                        aria-label="Периодичность"
                        className="text-left"
                        aria-describedby={props['aria-describedby']}
                        invalid={Boolean(shown.period)}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_PERIOD}>не задана</SelectItem>
                        {PERIODS.map((p) => (
                          <SelectItem key={p} value={p}>
                            {ECO_PERIOD_LABELS[p]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
              )}

              {!isDocument && input.period === 'yearly' ? (
                <Field
                  label="Дата сдачи (ДД.ММ)"
                  hint="Пусто — без фиксированной даты"
                  error={shown.deadline}
                  className="my-0"
                >
                  {(props) => (
                    <Input
                      {...props}
                      value={draft.deadline}
                      placeholder="25.03"
                      inputMode="numeric"
                      invalid={Boolean(shown.deadline)}
                      onChange={(e) => setDraft((d) => ({ ...d, deadline: e.target.value }))}
                    />
                  )}
                </Field>
              ) : null}

              <Field
                label="Пояснение к сроку"
                hint={
                  isDocument
                    ? 'Например: бессрочно при неизменности процессов'
                    : 'Например: договор на год'
                }
                className="my-0 nw:col-span-2"
              >
                {(props) => (
                  <Input
                    {...props}
                    value={input.validityNote}
                    onChange={(e) => patch({ validityNote: e.target.value })}
                  />
                )}
              </Field>

              <Field label="Срок разработки" className="my-0">
                {(props) => (
                  <Input
                    {...props}
                    value={input.developmentTerm}
                    placeholder="от 30 дней"
                    onChange={(e) => patch({ developmentTerm: e.target.value })}
                  />
                )}
              </Field>
              <Field label="Согласование" className="my-0">
                {(props) => (
                  <Input
                    {...props}
                    value={input.approval}
                    placeholder="Росприроднадзор"
                    onChange={(e) => patch({ approval: e.target.value })}
                  />
                )}
              </Field>
            </div>

            <Field
              label="Описание для клиента"
              hint="Подпись под названием в карточке услуги при заказе"
            >
              {(props) => (
                <Input
                  {...props}
                  value={input.clientDescription}
                  onChange={(e) => patch({ clientDescription: e.target.value })}
                />
              )}
            </Field>

            <div className="my-4">
              <p className="mb-1.5 text-13 text-txt-secondary">Обязателен для категорий</p>
              <div
                className="flex flex-wrap gap-2"
                role="group"
                aria-label="Обязателен для категорий"
              >
                {ECO_CATEGORIES.map((c) => {
                  const on = input.categories.includes(c)
                  return (
                    <Button
                      key={c}
                      type="button"
                      size="sm"
                      variant="secondary"
                      aria-pressed={on}
                      className={cn('min-w-12', on && SELECTED_OPTION)}
                      onClick={() =>
                        patch({
                          categories: toggleCanonical(ECO_CATEGORIES, input.categories, c, !on),
                        })
                      }
                    >
                      {ECO_CATEGORY_LABELS[c]}
                    </Button>
                  )
                })}
              </div>
            </div>

            {isDocument ? (
              <div className="my-4">
                <p className="mb-1.5 text-13 text-txt-secondary">Когда обновлять</p>
                <p className="mb-2.5 text-12 text-txt-tertiary">
                  Если клиент отметит такое изменение в ежегодном вопросе «что менялось», документ
                  получит статус «Требует актуализации»
                </p>
                <div className="flex flex-col gap-2.5">
                  {ECO_UPDATE_TRIGGERS.map((trigger) => (
                    <LabeledCheckbox
                      key={trigger}
                      checked={input.updateTriggers.includes(trigger)}
                      onCheckedChange={(on) =>
                        patch({
                          updateTriggers: toggleCanonical(
                            ECO_UPDATE_TRIGGERS,
                            input.updateTriggers,
                            trigger,
                            on,
                          ),
                        })
                      }
                    >
                      {ECO_UPDATE_TRIGGER_LABELS[trigger]}
                    </LabeledCheckbox>
                  ))}
                </div>
              </div>
            ) : null}

            {isDocument && dependencyOptions.length > 0 ? (
              <div className="my-4">
                <p className="mb-1.5 text-13 text-txt-secondary">
                  Обновлять после нового документа
                </p>
                <p className="mb-2.5 text-12 text-txt-tertiary">
                  Когда у клиента появится новый такой документ, этот получит статус «Требует
                  актуализации». Например, ПЭК, НДВ и НМУ делаются после новой инвентаризации
                  выбросов
                </p>
                <div className="flex flex-col gap-2.5">
                  {dependencyOptions.map((dep) => (
                    <LabeledCheckbox
                      key={dep.id}
                      checked={input.dependsOn.includes(dep.id)}
                      onCheckedChange={(on) =>
                        patch({
                          dependsOn: on
                            ? [...input.dependsOn, dep.id]
                            : input.dependsOn.filter((id) => id !== dep.id),
                        })
                      }
                    >
                      {dep.name}
                    </LabeledCheckbox>
                  ))}
                </div>
              </div>
            ) : null}

            {input.kind === 'document' ? (
              <Field
                label="Требует актуализации, если разработан до"
                hint="Сменились бланк или методика: паспорта отходов — 01.01.2014, планы НМУ — 01.03.2026. Пусто — правила нет"
              >
                {(props) => (
                  <DateInput
                    {...props}
                    value={input.outdatedBefore}
                    onChange={(outdatedBefore) => patch({ outdatedBefore })}
                  />
                )}
              </Field>
            ) : null}

            <div className="mt-4 flex flex-col gap-2.5 border-t border-line pt-4">
              <LabeledCheckbox
                checked={input.tracked}
                onCheckedChange={(tracked) => patch({ tracked })}
              >
                Ведётся строкой в пульте площадки
              </LabeledCheckbox>
              {item?.trainingLinked ? (
                <p className="text-13 text-txt-secondary">
                  В заказе экологии не показывается: строка пульта берётся из журнала обучения
                  (направление «Экология»), заказ оформляется в разделе «Обучение».
                </p>
              ) : (
                <>
                  <LabeledCheckbox
                    checked={input.showInOrder}
                    onCheckedChange={(showInOrder) => patch({ showInOrder })}
                  >
                    Показывать клиенту в заказе услуги
                  </LabeledCheckbox>
                  {input.showInOrder ? (
                    <LabeledCheckbox
                      align="start"
                      checked={input.orgWide}
                      onCheckedChange={(orgWide) => patch({ orgWide })}
                    >
                      В заказе заранее отмечены все площадки организации — клиент снимает лишние
                    </LabeledCheckbox>
                  ) : null}
                </>
              )}
            </div>

            {!item?.trainingLinked && input.showInOrder && questions.length > 0 ? (
              <div className="mt-4">
                <p className="mb-1.5 text-13 text-txt-secondary">Вопросы при заказе</p>
                <p className="mb-2 text-12 text-txt-tertiary">
                  Мастер заказа задаст их клиенту по этой услуге; ответы подставятся из опроса
                  площадки.
                </p>
                <div className="flex flex-col gap-2.5" role="group" aria-label="Вопросы при заказе">
                  {questions.map((q) => (
                    <LabeledCheckbox
                      key={q.id}
                      align="start"
                      checked={input.orderQuestionIds.includes(q.id)}
                      onCheckedChange={(on) =>
                        patch({
                          orderQuestionIds: on
                            ? [...input.orderQuestionIds, q.id]
                            : input.orderQuestionIds.filter((id) => id !== q.id),
                        })
                      }
                    >
                      {q.text}
                    </LabeledCheckbox>
                  ))}
                </div>
              </div>
            ) : null}
          </form>
        </Card>
      </DialogContent>
    </Dialog>
  )
}
